import React, { useMemo, useState } from 'react';
import { Button, Card, Badge, Input, EmptyState } from '@tadagram/ui';
import { useMarketplaceEngine } from '../hooks/useMarketplaceEngine';
import { ListingType } from '../domain/marketplaceTypes';
import { Search, Store } from 'lucide-react';

const CATEGORIES: { label: string; value: 'all' | ListingType }[] = [
  { label: 'Tất cả', value: 'all' },
  { label: 'Seeding Account', value: 'seeding' },
  { label: 'AI Account', value: 'ai' },
];

const GRADIENTS = [
  'bg-gradient-to-br from-purple-100 to-indigo-50',
  'bg-gradient-to-br from-pink-100 to-rose-50',
  'bg-gradient-to-br from-blue-100 to-indigo-50',
  'bg-gradient-to-br from-emerald-100 to-green-50',
  'bg-gradient-to-br from-orange-100 to-red-50',
  'bg-gradient-to-br from-violet-100 to-purple-50',
  'bg-gradient-to-br from-cyan-100 to-blue-50',
];
function cardGradient(id: string) {
  let h = 5381;
  for (let i = 0; i < id.length; i++) h = (h * 33) ^ id.charCodeAt(i);
  return GRADIENTS[Math.abs(h) % GRADIENTS.length];
}

function getPlatform(item: ListingType, title: string, platform?: string) {
  if (item !== 'seeding') return null;
  if (platform) return platform;
  const t = title.toLowerCase();
  if (t.includes('facebook') || t.includes('fb')) return 'facebook';
  if (t.includes('tiktok') || t.includes('tt')) return 'tiktok';
  if (t.includes('instagram') || t.includes('ig')) return 'instagram';
  if (t.includes('zalo')) return 'zalo';
  return 'khác';
}

const Browse: React.FC = () => {
  const { listings, isSyncing, purchaseListing } = useMarketplaceEngine();
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState<'all' | ListingType>('all');
  const [purchasingId, setPurchasingId] = useState<string | null>(null);

  const handlePurchase = async (listingId: string) => {
    if (confirm('Bạn có chắc chắn muốn mua sản phẩm này? Số tiền sẽ được tạm giữ an toàn trong Escrow.')) {
      setPurchasingId(listingId);
      const res = await purchaseListing(listingId);
      alert(res.message);
      setPurchasingId(null);
    }
  };

  const filtered = useMemo(() => {
    return listings.filter((item) => {
      if (item.status !== 'active') return false;
      if (category !== 'all' && item.type !== category) return false;
      const q = keyword.trim().toLowerCase();
      if (!q) return true;
      return item.title.toLowerCase().includes(q) || item.seller.toLowerCase().includes(q);
    });
  }, [listings, category, keyword]);

  return (
    <div className="flex flex-col gap-6">
      {/* Search + filter bar */}
      <Card className="p-5 flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={keyword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
            placeholder="Tìm theo tên sản phẩm hoặc người bán..."
            className="pl-9 w-full bg-slate-50"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === cat.value
                  ? 'bg-blue-500 text-white border border-blue-500'
                  : 'bg-white text-slate-500 border border-slate-200 hover:border-blue-300'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Sync notice */}
      {isSyncing && (
        <p className="text-center text-sm text-slate-500 my-2">
          Đang tải dữ liệu từ server...
        </p>
      )}

      {/* Empty state */}
      {filtered.length === 0 && !isSyncing && (
        <Card className="py-16 text-center text-slate-500 flex flex-col items-center justify-center">
          <Store className="w-12 h-12 mb-3 opacity-40" />
          <p className="text-base font-medium">Không có sản phẩm nào phù hợp</p>
        </Card>
      )}

      {/* Product grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((item) => (
          <Card key={item.id} className="p-4 flex flex-col gap-3 group hover:shadow-md transition-shadow">
            {/* Banner */}
            <div
              className={`rounded-xl p-6 text-center text-2xl font-bold tracking-wide text-slate-800 ${cardGradient(
                item.id
              )}`}
            >
              {item.banner || item.title.slice(0, 2).toUpperCase()}
            </div>

            {/* Category badge */}
            <div className="flex gap-2 items-center flex-wrap">
              <Badge variant={item.type === 'ai' ? 'primary' : 'success'}>
                {item.type === 'ai' ? 'AI Account' : 'Seeding Account'}
              </Badge>
              {getPlatform(item.type, item.title, item.platform) && (
                <Badge variant="neutral" className="uppercase text-[10px]">
                  {getPlatform(item.type, item.title, item.platform)}
                </Badge>
              )}
            </div>

            <h3 className="text-base font-bold text-slate-900 m-0">
              {item.title}
            </h3>
            <p className="text-xs text-slate-500 m-0">Người bán: {item.seller}</p>
            {item.description && (
              <p className="text-sm text-slate-600 m-0 line-clamp-2">
                {item.description}
              </p>
            )}

            <p className="mt-1 mb-0 text-sm text-slate-900">
              <strong className="text-base text-blue-600 font-bold">
                {item.credits.toLocaleString('vi-VN')}
              </strong>{' '}
              Tada Credits
            </p>

            <div className="flex gap-2 mt-auto pt-2">
              <Button 
                size="sm" 
                onClick={() => handlePurchase(item.id)}
                disabled={purchasingId === item.id}
              >
                {purchasingId === item.id ? 'Đang xử lý...' : 'Mua ngay'}
              </Button>
              <Button size="sm" variant="secondary">Thương lượng</Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Browse;
